'use client';

import type { UIMessage, UseChatHelpers } from '@ai-sdk/react';
import type { NodeEntry } from '@udecode/plate';
import { useEditorPlugin, usePluginOption } from '@udecode/plate/react';
import { BlockSelectionPlugin } from '@udecode/plate-selection/react';
import { useEffect } from 'react';

import { AIChatPlugin } from '../AIChatPlugin';

export type UseEditorChatOptions = {
  chat: UseChatHelpers<UIMessage>;
  onOpenBlockSelection?: (blocks: NodeEntry[]) => void;
  onOpenChange?: (open: boolean) => void;
  onOpenCursor?: () => void;
  onOpenSelection?: () => void;
};

export const useEditorChat = ({
    chat,
    onOpenBlockSelection,
    onOpenChange,
    onOpenCursor,
    onOpenSelection,
}: UseEditorChatOptions) => {
    const { editor, setOption } = useEditorPlugin(AIChatPlugin);
    const open = usePluginOption(AIChatPlugin, 'open');

    // Sync useChat with AIChatPlugin
    useEffect(() => {
        setOption('chat', chat);
    }, [chat.messages, chat.status, chat.messages, chat.error]);

    useEffect(() => {
        onOpenChange?.(open);

        if (open) {
            if (onOpenBlockSelection) {
                const blockSelectionApi = editor.getApi(BlockSelectionPlugin).blockSelection;
                const isBlockSelecting = editor.getOption(
                    BlockSelectionPlugin,
                    'isSelectingSome'
                );

                if (isBlockSelecting) {
                    onOpenBlockSelection(blockSelectionApi.getNodes());

                    return;
                }
            }
            if (onOpenCursor && editor.api.isCollapsed()) {
                onOpenCursor();

                return;
            }
            if (onOpenSelection && editor.api.isExpanded()) {
                onOpenSelection();

                return;
            }
            // Fallback: no selection (editor not focused) — treat as cursor open
            onOpenCursor?.();
        }
    }, [open]);
};
